import ValidationService from '../../../../../common/services/validation';
import IContact, {
    ContactType,
    ICarriersContact,
} from '../../../../../common/interfaces/IContacts';
import IBranchFormData from '../../../../../common/interfaces/IBranchFormData';
import { IInvalidCodesResponse } from '../../../interfaces/IInvalidCodesResponse';
import { IRegionLocations } from '../../../../../common/interfaces/IRegionLocations';
import {
    BranchCodesValidationSchema,
    GeneralInfoValidationSchema,
} from './branch-form.validation.service';
import { getCarrierAxiosInstance } from '../../../../../common/services/axios-config';
import IValidateCodesRequest from '../../../interfaces/IValidateCodesRequest';
import ISaveBranchRequest from '../../../interfaces/ISaveBranchRequest';
import ISaveBranchContactRequest from '../../../interfaces/ISaveBranchContactRequest';
import IUpdateBranchLocationsRequest from '../../../interfaces/IUpdateBranchLocationsRequest';
import IValidationResult from '../../../../../common/interfaces/IValidationResult';
import {
    DesserializerDecimal,
    SerializerDecimal,
} from '../../../../../utils/formatterDecimal';
import { ICarrierData } from '../../../interfaces/ICarrierData';
import { normalizeCNPJ } from '../../../../../utils/cnpj-utils';

const getRegionsList = async (): Promise<IRegionLocations[]> => {
    const Api = await getCarrierAxiosInstance();

    const response = await Api.get('v1/locations').catch(() => {
        throw new Error('Ocorreu um problema ao consultar as localizações');
    });

    if (response.data) {
        const { data } = response;
        return data;
    }

    return [] as IRegionLocations[];
};

const getCarriersInfo = async (carrierId: string): Promise<ICarrierData> => {
    const api = await getCarrierAxiosInstance();
    const response = await api.get(`v1/carriers/${carrierId}`).catch(() => {
        throw new Error(
            'Ocorreu um problema ao consultar o contato principal da matriz',
        );
    });

    if (response.data?.contacts) {
        const { contacts } = response.data;
        const mainContact =
            contacts.find((x: ICarriersContact) => x.mainContact) || {};

        return {
            carrierContact: mainContact,
            paymentTerm: DesserializerDecimal(response.data.paymentTerm),
        } as ICarrierData;
    }

    return {} as ICarrierData;
};

const checkInvalidCodes = async (
    eccCode: string,
    sapCode: string,
    tmsCode: string,
    branchId?: string,
): Promise<IInvalidCodesResponse> => {
    const api = await getCarrierAxiosInstance();
    const request: IValidateCodesRequest = {
        eccCode,
        tmsCode,
        sapCode,
        branchId,
    };

    const response = await api
        .get('v1/branches:validateCodes', { params: request })
        .catch(() => {
            throw new Error(
                'Ocorreu um problema ao tentar validar os códigos informados',
            );
        });

    return {
        eccCode: !response.data.eccCode,
        sapCode: !response.data.sapCode,
        tmsCode: !response.data.tmsCode,
    } as IInvalidCodesResponse;
};

const validateGeneralInfo = (
    branchData: IBranchFormData,
    isMainContactRequired = true,
): IValidationResult => {
    return ValidationService.validate(
        GeneralInfoValidationSchema(isMainContactRequired),
        branchData,
    );
};

const validateSystemCodes = async (
    branchData: IBranchFormData,
): Promise<IValidationResult> => {
    const codesValidationResult = await checkInvalidCodes(
        branchData.eccCode,
        branchData.sapCode,
        branchData.tmsCode,
        branchData.id,
    );

    return ValidationService.validate(
        BranchCodesValidationSchema,
        codesValidationResult,
    );
};

function createContact(
    contact: IContact,
    mainContact: boolean,
): ISaveBranchContactRequest {
    return {
        phone: contact.phone,
        email: contact.email,
        occupation: contact.occupation ?? '',
        responsible: contact.responsible ?? '',
        contactType: contact.contactType ?? ContactType.operational,
        mainContact,
    } as ISaveBranchContactRequest;
}

const saveCarrierBranch = async (
    carrierId: string,
    branchData: IBranchFormData,
    branchId?: string,
): Promise<string> => {
    const api = await getCarrierAxiosInstance();

    const contacts: ISaveBranchContactRequest[] = [];

    contacts.push(createContact(branchData.mainContact, true));

    branchData.additionalContacts.forEach((additionalContact: IContact) => {
        contacts.push(createContact(additionalContact, false));
    });

    const request: ISaveBranchRequest = {
        cnpj: normalizeCNPJ(branchData.cnpj),
        corporateName: branchData.corporateName,
        tmsCode: branchData.tmsCode,
        sapCode: branchData.sapCode,
        eccCode: branchData.eccCode,
        contacts,
        paymentTerm: SerializerDecimal(branchData.paymentTerm),
    } as ISaveBranchRequest;

    try {
        const { data } = branchId
            ? await api.put(
                  `v1/carriers/${carrierId}/branches/${branchId}`,
                  request,
              )
            : await api.post(`v1/carriers/${carrierId}/branches`, request);

        return data;
    } catch {
        const errorMessageVariant = branchId
            ? 'atualizar a'
            : 'adicionar a nova';
        throw new Error(
            `Ocorreu um problema ao tentar ${errorMessageVariant} filial`,
        );
    }
};

const updateBranchLocations = async (
    carrierId: string,
    branchId: string,
    locationsIds: string[],
): Promise<string> => {
    const api = await getCarrierAxiosInstance();

    const request: IUpdateBranchLocationsRequest = {
        locationsIds,
    } as IUpdateBranchLocationsRequest;

    const response = await api
        .put(`v1/carriers/${carrierId}/branches/${branchId}/locations`, request)
        .catch(() => {
            throw new Error('Não foi possível adicionar as regiões à filial');
        });

    return response.data;
};

const BranchFormService = {
    getCarriersInfo,
    validateGeneralInfo,
    validateSystemCodes,
    getRegionsList,
    saveCarrierBranch,
    updateBranchLocations,
};

export default BranchFormService;
